'use client';

import { TheBoatAppLandingConfigType, TheBoatDBConfigType, useConfig } from '@marine/configs';
import { Col, Grid, Row, Typography } from 'antd';
import { stegaClean } from 'next-sanity';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { SanityImage } from 'sanity-image';
import { CustomBreadcrumb } from '../../CustomBreadcrumb';
import { LinkItem } from '../../CustomBreadcrumb/CustomBreadcrumb.props';
import { AllFeaturesQueryResult } from '../../sanity_types/theboat_landing_types';
import styles from './AllFeaturesPage.module.scss';
import SingleFeature from './SingleFeature';

type SingleFeatureInterface = AllFeaturesQueryResult[number];

interface FeaturePage extends SingleFeatureInterface {}

interface AllFeaturesPageProps {
  data: FeaturePage[];
  /**
   * The page's h1.
   *
   * A prop rather than a literal because this component backs the features page on both
   * theboatapp.com and theboatdb.com. The default is what both shipped before — "A sea of
   * features", which at 17 characters reads as a tagline and names neither the product nor what
   * the features are for. TheBoatApp overrides it; TheBoatDB keeps the default until its own
   * audit says otherwise.
   */
  heading?: string;
}

const { Paragraph } = Typography;

export const AllFeaturesPage: FC<AllFeaturesPageProps> = ({ data, heading = 'A sea of features' }) => {
  const config = useConfig<TheBoatAppLandingConfigType | TheBoatDBConfigType>();
  const { md } = Grid.useBreakpoint();
  const [activeFeaturePage, setActiveFeaturePage] = useState<FeaturePage | undefined>();
  const imageId = useMemo(() => stegaClean(activeFeaturePage?.image?.asset?._ref || ''), [activeFeaturePage]);

  const breadcrumbs: LinkItem[] = [
    {
      href: '/',
      slug: 'home',
      title: 'Home',
    },
    {
      href: '/features',
      slug: 'features',
      title: 'Features',
    },
  ];

  useEffect(() => {
    if (data?.length > 0) {
      setActiveFeaturePage(data[0]);
    }
  }, [data]);

  const handleItemClick = useCallback((dataInput: FeaturePage) => {
    setActiveFeaturePage(dataInput);
  }, []);

  return (
    <div className={styles.featuresPageContainer}>
      <div className={styles.breadcrumbContainer}>
        <CustomBreadcrumb items={breadcrumbs} />
      </div>

      <div>
        <h1 className={styles.featuresTitleContainer}>{heading}</h1>
      </div>

      <div>
        <Paragraph className={styles.featuresDescriptionContainer}>
          With logbook, inventory, documents, checklists, tasks and alerts over cloud services, TheBoatApp, powered by
          TheBoatDB, is all about having the best experience in managing your boat efficiently and effectively.
        </Paragraph>
      </div>

      {md ? (
        <Row style={{ display: 'flex', gap: '20px' }}>
          <Col span={md ? 14 : 24} className={styles.featuresPageItemsContainer}>
            {data?.map((featurePage) => (
              <SingleFeature
                key={featurePage?._id}
                data={featurePage}
                isActive={activeFeaturePage?._id === featurePage?._id}
                handleItemClick={handleItemClick}
              />
            ))}
          </Col>

          <Col span={md ? 9 : 24} className={styles.imageContainer}>
            {activeFeaturePage?.image?.asset?._ref && (
              <div style={{ height: '100%', position: 'relative' }}>
                <SanityImage
                  id={imageId}
                  mode="cover"
                  // Names the feature the panel is currently showing. Both branches hard-coded "Features
                  // cover image", so the alt described neither the image nor which feature it belonged to.
                  alt={activeFeaturePage?.title ? `${activeFeaturePage.title} preview` : ''}
                  className={styles.featuresPageImage}
                  dataset={config.dataset}
                  projectId={config.projectId}
                  queryParams={{ q: 80 }}
                />
              </div>
            )}
          </Col>
        </Row>
      ) : (
        <Row style={{ display: 'flex', gap: '20px' }}>
          <Col span={md ? 9 : 24} className={styles.imageContainer}>
            {activeFeaturePage?.image?.asset?._ref && (
              <div style={{ height: '100%', position: 'relative' }}>
                <SanityImage
                  id={imageId}
                  mode="cover"
                  // Names the feature the panel is currently showing. Both branches hard-coded "Features
                  // cover image", so the alt described neither the image nor which feature it belonged to.
                  alt={activeFeaturePage?.title ? `${activeFeaturePage.title} preview` : ''}
                  className={styles.featuresPageImage}
                  dataset={config.dataset}
                  projectId={config.projectId}
                  queryParams={{ q: 80 }}
                />
              </div>
            )}
          </Col>
          <Col span={md ? 14 : 24} className={styles.featuresPageItemsContainer}>
            {data?.map((featurePage) => (
              <SingleFeature
                key={featurePage?._id}
                data={featurePage}
                isActive={activeFeaturePage?._id === featurePage?._id}
                handleItemClick={handleItemClick}
              />
            ))}
          </Col>
        </Row>
      )}
    </div>
  );
};
